import React, { useEffect, useState } from "react";
import classes from "./PreLogin.module.scss";
import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Group,
  Image,
  Pagination,
  ScrollArea,
  SegmentedControl,
  Space,
  Stack,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import {
  IconArrowUpRight,
  IconDownload,
  IconSearch,
} from "@tabler/icons-react";
import ImgJavis from "@/assets/images/pngs/img-javis.png";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { get } from "lodash";
import { useDebouncedValue } from "@mantine/hooks";
import {
  useGetWebUserGuideQuery,
  useGetFaqCategoryQuery,
  useGetFAQsQuery,
} from "@/services/authAPI";
import { FAQItem } from "@/dto/auth/response/auth-response";
import LanguageSelect from "@/components/utils/language-picker/LanguageSelect";

const FAQ_PAGE_SIZE = 8;
const GUIDE_PAGE_SIZE = 10;

type FaqCategory = {
  faqCategoryId: number;
  name: string;
  /** Bản tiếng Anh backend đã trả sẵn (nếu quản trị đã nhập nội dung EN). */
  nameEn?: string;
};

const SupportWrapper: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isEnglish = (i18n.language || "vi").startsWith("en");
  /**
   * Nội dung màn Hỗ trợ (danh mục câu hỏi, câu hỏi, tài liệu hướng dẫn) đều được
   * backend trả kèm bản tiếng Anh. Trước đây màn này chỉ đọc trường tiếng Việt nên
   * bật tiếng Anh vẫn ra tiếng Việt. Thiếu bản EN thì lùi về bản gốc.
   */
  const pickEn = (vn?: string, en?: string): string =>
    (isEnglish && en) || vn || "";
  const navigate = useNavigate();
  const [value, setValue] = useState("question");

  const { data: guidesData } = useGetWebUserGuideQuery();
  const guides = get(guidesData, "body.webUserGuides", []);

  const { data: faqData, isLoading } = useGetFaqCategoryQuery();
  const categories = get(faqData, "body.faqCategories", []) as FaqCategory[];

  const [faqCategoryId, setFaqCategoryId] = useState<number | null>(null);
  const [faqSearch, setFaqSearch] = useState("");
  const [debouncedFaqSearch] = useDebouncedValue(faqSearch, 400);
  const [faqPage, setFaqPage] = useState(1);

  const [guideSearch, setGuideSearch] = useState("");
  const [debouncedGuideSearch] = useDebouncedValue(guideSearch, 400);
  const [guidePage, setGuidePage] = useState(1);

  const handleSelectCategory = (id: number) => {
    setFaqCategoryId(id);
    setFaqSearch("");
    setFaqPage(1);
  };

  useEffect(() => {
    setFaqPage(1);
  }, [debouncedFaqSearch]);

  useEffect(() => {
    setGuidePage(1);
  }, [debouncedGuideSearch]);

  const { data: faqsResponse } = useGetFAQsQuery(
    faqCategoryId !== null
      ? {
          faqCategoryId,
          page: faqPage - 1,
          pageSize: FAQ_PAGE_SIZE,
          q: debouncedFaqSearch,
        }
      : (undefined as never),
    { skip: faqCategoryId === null },
  );

  const faqs = get(faqsResponse, "body.faqs", []) as FAQItem[];
  const totalFaqPages = get(faqsResponse, "body.totalPages", 1) as number;

  if (isLoading || !categories || categories.length === 0) {
    return null;
  }

  const handleDownload = (item: any) => {
    const link = document.createElement("a");
    link.href = (isEnglish && item?.fileEN) || item?.file;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredGuides = guides.filter((item) =>
    (item.name ?? "").toLowerCase().includes(debouncedGuideSearch.toLowerCase()),
  );
  const totalGuidePages = Math.max(
    1,
    Math.ceil(filteredGuides.length / GUIDE_PAGE_SIZE),
  );
  const paginatedGuides = filteredGuides.slice(
    (guidePage - 1) * GUIDE_PAGE_SIZE,
    guidePage * GUIDE_PAGE_SIZE,
  );

  return (
    <Box style={{ background: "#f3f4f6" }}>
      <Box className={classes.supportHearder}>
        <Box p={20} pos="absolute" top={0} w="calc(100% - 48px)">
          <Box className={classes.headerPreLogin}>
            <Box w={166}>
              <Image src={ImgJavis} alt="Javis Logo" w={166} />
            </Box>
            <Flex gap={40} align="center">
              <ActionIcon>
                <IconSearch />
              </ActionIcon>
              <Text fw={700} fz={16} c="#15171E" style={{ cursor: "pointer" }}>
                {t("FAQ_MENU")}
              </Text>

              {/* #56178: khối này trước đây là bộ chọn ngôn ngữ GIẢ — Menu.Dropdown chỉ có
                  đúng một mục "Tiếng Việt" và không có onClick, nên màn Hỗ trợ không đổi được
                  sang tiếng Anh. Dùng chung <LanguageSelect /> như các màn pre-login khác. */}
              <LanguageSelect />

              <Button
                rightIcon={<IconArrowUpRight />}
                h={56}
                onClick={() => navigate("/login")}
                style={{
                  background:
                    "linear-gradient(279.48deg, #08AAF3 -9.02%, #2A81D0 10.15%, #10436E 99.12%)",
                }}
              >
                {t("LOGIN_NOW")}
              </Button>
            </Flex>
          </Box>
        </Box>
        <Box className={classes.supportEnd}>
          <Text fw={500} style={{ color: "#99A0AE" }}>
            {t("HOME")}
          </Text>
          <Space h={20} />
          <Text fw={700} fz={40} style={{ color: "white" }}>
            {t("SUPPORT_INFO_PAGE")}
          </Text>
          <Space h={8} />
          <Text fz={20} style={{ color: "#FFFFFFCC" }}>
            {t("SEARCH_FAQ_HINT")}
          </Text>
          <Space h={20} />
          <SegmentedControl
            value={value}
            onChange={setValue}
            styles={{
              root: { borderRadius: "20px" },
              indicator: { borderRadius: "40px" },
            }}
            data={[
              { label: `${t("FAQ")}`, value: "question" },
              { label: `${t("USER_GUIDE")}`, value: "guide" },
            ]}
          />
        </Box>
      </Box>
      <Space h={20} />
      <ScrollArea h={635}>
        {value === "question" && (
          <Box
            style={{ background: "#FFFFFF", borderRadius: "16px" }}
            mx={24}
            p={24}
          >
            <Tabs
              value={faqCategoryId !== null ? String(faqCategoryId) : null}
              onTabChange={(tabValue) => {
                if (tabValue) handleSelectCategory(Number(tabValue));
              }}
            >
              <Tabs.List>
                {categories.map((cat) => (
                  <Tabs.Tab
                    key={cat.faqCategoryId}
                    value={String(cat.faqCategoryId)}
                    onClick={() => handleSelectCategory(cat.faqCategoryId)}
                  >
                    {pickEn(cat.name, cat.nameEn)}
                  </Tabs.Tab>
                ))}
              </Tabs.List>

              {faqCategoryId !== null ? (
                <Tabs.Panel value={String(faqCategoryId)} pt="xs">
                  <Flex align="center" justify="space-between">
                    <TextInput
                      w={678}
                      radius={8}
                      placeholder={`${t("SEARCH")}`}
                      icon={<IconSearch width={16} />}
                      value={faqSearch}
                      onChange={(e) => setFaqSearch(e.currentTarget.value)}
                    />
                    <Pagination
                      total={totalFaqPages || 1}
                      value={faqPage}
                      onChange={setFaqPage}
                      withEdges
                    />
                  </Flex>

                  <Space h={16} />

                  <Flex gap={16} wrap="wrap">
                    {faqs.map((faq, idx) => (
                      <Flex
                        key={faq.id}
                        gap={16}
                        justify="flex-start"
                        w="48%"
                        style={{ marginBottom: 16 }}
                      >
                        <Box
                          style={{
                            background: "#DEF2FD",
                            padding: "10px 16px",
                            borderRadius: 8,
                            fontWeight: 500,
                            height: 46,
                          }}
                        >
                          {(faqPage - 1) * FAQ_PAGE_SIZE + idx + 1}
                        </Box>

                        <Stack spacing={8}>
                          <Text fw={600} fz={16} style={{ color: "#010F1D" }}>
                            {pickEn(faq.title, (faq as { titleEn?: string }).titleEn)}
                          </Text>

                          <Text fz={16} style={{ color: "#6C727F" }}>
                            {pickEn(faq.content, (faq as { contentEn?: string }).contentEn)}
                          </Text>
                        </Stack>
                      </Flex>
                    ))}
                  </Flex>
                </Tabs.Panel>
              ) : (
                <Box py="xl">
                  <Text ta="center" c="#6C727F">
                    {t("PICK_CATEGORY_TO_VIEW_FAQ")}
                  </Text>
                </Box>
              )}
            </Tabs>
          </Box>
        )}
        {value === "guide" && (
          <Box
            style={{ background: "#FFFFFF", borderRadius: "16px" }}
            mx={24}
            p={24}
          >
            <Tabs defaultValue="gallery">
              <Tabs.List>
                <Tabs.Tab value="gallery">{t("CARDS_TAB")}</Tabs.Tab>
                <Tabs.Tab value="messages">{t("DIGITAL_BANKING_TAB")}</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="gallery" pt="xs">
                <Flex align="center" justify="space-between">
                  <TextInput
                    w={678}
                    radius={8}
                    placeholder={`${t("SEARCH")}`}
                    icon={<IconSearch width={16} />}
                    value={guideSearch}
                    onChange={(e) => setGuideSearch(e.currentTarget.value)}
                  />
                  <Pagination
                    total={totalGuidePages}
                    value={guidePage}
                    onChange={setGuidePage}
                    withEdges
                  />
                </Flex>

                <Space h={16} />
                <Flex gap={16} wrap="wrap">
                  {paginatedGuides.map((item) => (
                    <Box
                      key={item.name}
                      style={{
                        padding: "24px",
                        borderRadius: "12px",
                        width: "48%",
                        border: "1px solid #E5E7EB",
                      }}
                    >
                      <Flex align="center" justify="space-between">
                        <Box>
                          <Text fw={600} fz={20} style={{ color: "#010F1D" }}>
                            {pickEn(item.name, (item as { nameEN?: string }).nameEN)}
                          </Text>
                          <Text style={{ color: "#99A0AE" }}>
                            {t("UPLOAD_DATE_COLON")}
                            <span style={{ fontWeight: 500, color: "#6C727F" }}>
                              {item.effectDate}
                            </span>
                          </Text>
                        </Box>
                        <Group spacing={6} onClick={() => handleDownload(item)}>
                          <ActionIcon
                            variant="subtle"
                            color="blue"
                            component="a"
                            href="#"
                            title={`${t("DOWNLOAD")}`}
                          >
                            <IconDownload size={18} />
                          </ActionIcon>
                          <Text
                            size="sm"
                            c="blue"
                            style={{ cursor: "pointer" }}
                          >
                            {t("DOWNLOAD")}
                          </Text>
                        </Group>
                      </Flex>
                    </Box>
                  ))}
                </Flex>
              </Tabs.Panel>
            </Tabs>
          </Box>
        )}
      </ScrollArea>
    </Box>
  );
};

export default SupportWrapper;
